/**
 * One report, drawn.
 *
 * A report page is the same thing whether a signed-in client, a staff member or the holder of
 * a secret link opens it, so everything above the map works from one view model: the kind, the
 * name, its configuration and the objects the page may draw. `ReportPage` dispatches on
 * the kind; the kind pages only arrange the map.
 */
import { useCallback, useMemo, useState } from 'react';
import { Group, Stack, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { PhotoPositioning, PublicReport, Report, ReportKind } from '@/api/reports';
import type { GrantedObject, ViewerLink } from '@/api/viewer';
import { EmptyState } from '@/components/EmptyState';
import { Eyebrow } from '@/components/Eyebrow';
import { VectorLoadProgress } from './VectorLoadProgress';
import { useFormat } from '@/components/useFormat';
import type { LayerOptions, LayerRef, ObjectKind, PhotoObjectRef, Via } from '@/map/layers';
import type { LinkSource } from '@/map/linkSource';
import type { PhotoSourceError } from '@/map/photoSource';
import type { TokenSource } from '@/map/tokenSource';
import { VECTOR_MAX_MB, type VectorProgress, type VectorSourceError } from '@/map/vectorSource';
import { brand } from '@/theme/theme';
import { CrownHealthReport } from './CrownHealthReport';
import { ExportButton } from './ExportButton';
import { GrowingStockReport } from './GrowingStockReport';
import { MlsCoverageReport } from './MlsCoverageReport';
import { MultispectralReport } from './MultispectralReport';
import { OrthophotoReport } from './OrthophotoReport';
import { PhotoPointsReport } from './PhotoPointsReport';
import { ReportOptionsPanel } from './ReportOptionsPanel';
import { TreesReport } from './TreesReport';
import { useStoredMapView } from './useStoredMapView';

export interface ReportInputView {
  role: string;
  /** The data object bound to the role. */
  object: string;
  kind: ObjectKind;
  available: boolean;
  /** An acquisition's tilt limit as its preprocess run applied it; `null` when it did not say. */
  maxTiltDeg?: number | null;
  /** An orthophoto's photogrammetry GSD (cm/px) as its odm run measured it; `null` when unknown. */
  gsdCm?: number | null;
  /** Where an acquisition's camera positions come from; `null` for a preprocess run before that. */
  positioning?: PhotoPositioning | null;
}

/** The report as a page needs it, with nothing in it that a secret link must not learn (N13). */
export interface ReportView {
  kind: ReportKind;
  name: string;
  config: Record<string, unknown>;
  gone: boolean;
  inputs: ReportInputView[];
}

export interface KindReportProps {
  refs: LayerRef[];
  /** The report's bound objects; a page whose data is not a granted layer finds its object here. */
  inputs?: ReportInputView[];
  photos: PhotoObjectRef[];
  linkSource: LinkSource;
  onPhotoError: (error: PhotoSourceError) => void;
  onVectorError: (object: string, error: VectorSourceError) => void;
  onVectorProgress?: (object: string, progress: VectorProgress | null) => void;
  config: Record<string, unknown>;
  options: LayerOptions;
  onOptionsChange: (options: LayerOptions) => void;
  tokenSource: TokenSource;
}

export function toReportView(report: Report): ReportView {
  const state = report.render_state.inputs;
  return {
    kind: report.kind,
    name: report.name ?? '',
    config: (report.options ?? {}) as Record<string, unknown>,
    gone: report.render_state.gone,
    inputs: report.inputs.map((i) => ({
      role: i.role,
      object: i.object.public_id,
      kind: i.object.kind as ObjectKind,
      available: state[i.role]?.available ?? false,
      maxTiltDeg: i.object.max_tilt_deg,
      gsdCm: i.object.gsd_cm,
      positioning: i.object.positioning,
    })),
  };
}

export function fromPublicReport(payload: PublicReport): ReportView {
  return {
    kind: payload.kind as ReportKind,
    name: payload.name,
    config: (payload.options ?? {}) as Record<string, unknown>,
    gone: payload.gone,
    inputs: Object.entries(payload.inputs)
      .filter(([, i]) => i.object !== null)
      .map(([role, i]) => ({
        role,
        object: i.object!.public_id,
        kind: i.object!.kind as ObjectKind,
        available: i.available,
        maxTiltDeg: i.object!.max_tilt_deg ?? null,
        gsdCm: i.object!.gsd_cm ?? null,
        positioning: i.object!.positioning ?? null,
      })),
  };
}

/** The objects a token granted, each tagged with the object kind the report says it is. */
export function layerRefs(granted: GrantedObject[] | undefined, report: ReportView): LayerRef[] {
  const kinds = new Map(report.inputs.filter((i) => i.available).map((i) => [i.object, i.kind]));
  return (granted ?? [])
    .filter((g) => kinds.has(g.object))
    .map((g) => ({ object: g.object, object_kind: kinds.get(g.object)!, via: g.via as Via, path: g.path, digest: g.digest }));
}

/** The acquisitions a report may draw from the bucket: its available drone-photo inputs. */
export function photoRefs(report: ReportView): PhotoObjectRef[] {
  return report.inputs
    .filter((i) => i.available && i.kind === 'drone_photos_raw')
    .map((i) => ({ object: i.object, object_kind: 'drone_photos_raw' as const, maxTiltDeg: i.maxTiltDeg ?? null, positioning: i.positioning ?? null }));
}

export interface ReportPageProps {
  report: ReportView;
  refs: LayerRef[];
  tokenSource: TokenSource;
  linkSource: LinkSource;
  /** A signed-in viewer of the project: may see the figures and export. */
  editable?: boolean;
  /** Where this viewer's map adjustments are kept in the browser (see useStoredMapView). */
  viewKey: string;
  /** Extra buttons in the header, such as staff's report settings. */
  actions?: React.ReactNode;
  /** The viewer token could not be minted, so there is nothing to draw with. */
  tokenError?: boolean;
  /** What the viewer token granted; the export needs the trees object's file path from it. */
  granted?: GrantedObject[];
  /** Signs a bucket link to one file of an object, as for the map. */
  fetchLink?: (object: string, path: string) => Promise<ViewerLink>;
}

const PAGES: Record<ReportKind, (props: KindReportProps) => React.ReactElement> = {
  photo_points: PhotoPointsReport,
  orthophoto: OrthophotoReport,
  trees: TreesReport,
  mls_coverage: MlsCoverageReport,
  growing_stock: GrowingStockReport,
  multispectral: MultispectralReport,
  crown_health: CrownHealthReport,
};

export function ReportPage({
  report,
  refs,
  tokenSource,
  linkSource,
  editable = false,
  viewKey,
  actions,
  tokenError = false,
  granted,
  fetchLink,
}: ReportPageProps) {
  const { t } = useTranslation('reports');
  const { date, number } = useFormat();
  const [options, setOptions, resetOptions] = useStoredMapView(viewKey);
  const [photoError, setPhotoError] = useState<PhotoSourceError | null>(null);
  const [vectorError, setVectorError] = useState<VectorSourceError | null>(null);
  const [vectorProgress, setVectorProgress] = useState<Record<string, VectorProgress>>({});
  const onVectorProgress = useCallback((object: string, p: VectorProgress | null) => {
    setVectorProgress((prev) => {
      if (p === null) {
        if (!(object in prev)) return prev;
        const rest = { ...prev };
        delete rest[object];
        return rest;
      }
      return { ...prev, [object]: p };
    });
  }, []);
  // A fresh array would make the map fetch the photo points again on every render.
  const photos = useMemo(() => photoRefs(report), [report]);

  const layerOptions: LayerOptions = { ...options, baseUrl: tokenSource.baseUrl };
  // The tiler's origin comes from the token, so it is never part of what the report stores.
  const change = (next: LayerOptions) => {
    const rest: Partial<LayerOptions> = { ...next };
    delete rest.baseUrl;
    setOptions(rest);
  };

  const Page = PAGES[report.kind];
  const trees = report.inputs.find((i) => i.role === 'trees' && i.available);
  const treesPath = trees && granted?.find((g) => g.object === trees.object)?.path;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={4}>
          <Eyebrow>{t(`kinds.${report.kind}`)}</Eyebrow>
          <Title order={1}>{report.name || t(`kinds.${report.kind}`)}</Title>
          <span className="report-printed-on" style={{ fontSize: 12, color: brand.textMuted }}>
            {t('page.printedOn', { date: date(new Date().toISOString()) })}
          </span>
        </Stack>
        <Group gap="md" align="flex-end" className="report-actions">
          <ReportOptionsPanel onReset={resetOptions} onPrint={() => window.print()} />
          {actions}
          {editable && report.kind === 'trees' && trees && treesPath && fetchLink && (
            <ExportButton objectId={trees.object} path={treesPath} fetchLink={fetchLink} />
          )}
        </Group>
      </Group>

      {report.gone ? (
        <EmptyState title={t('gone.title')} description={t('gone.description')} />
      ) : tokenError ? (
        <EmptyState title={t('page.tokenError')} />
      ) : photoError ? (
        <EmptyState title={t('page.photosUnreadable')} />
      ) : (
        <>
          {/* Above the map, not instead of it: the rasters still draw. */}
          {vectorError && (
            <EmptyState
              title={t('page.vectorUnreadable')}
              description={
                vectorError.code === 'too_large' && vectorError.size !== undefined
                  ? t('page.vectorTooLarge', { size: number(vectorError.size / 1048576, 0), limit: VECTOR_MAX_MB })
                  : vectorError.code === 'unsupported_crs'
                    ? t('page.vectorUnsupportedCrs', { crs: vectorError.message })
                    : undefined
              }
            />
          )}
          <VectorLoadProgress progress={vectorProgress} />
          <Page
            refs={refs}
            inputs={report.inputs}
            photos={photos}
            linkSource={linkSource}
            onPhotoError={setPhotoError}
            onVectorError={(_, e) => setVectorError(e)}
            onVectorProgress={onVectorProgress}
            config={report.config}
            options={layerOptions}
            onOptionsChange={change}
            tokenSource={tokenSource}
          />
        </>
      )}
    </Stack>
  );
}
